/**
 * Adaptateur - RealBiometricFingerprintAdapter
 *
 * Intégration réelle avec le service FingerAuth (http://localhost:9000/Authentication)
 * Utilise les APIs du scanner biométrique Securgen
 *
 * CORRECTIONS APPLIQUÉES:
 * - Décodage Base64 des données carte (FingerImpl encode en Base64)
 * - Fallback robuste: si les données carte ne contiennent pas clientId/clientName,
 *   on utilise le numéro de carte comme identifiant
 * - La vérification biométrique ne doit PAS échouer à cause du parsing des données carte
 * - Force stop+start pour ré-authentifier la carte (corrige 6A86 après enrollment)
 * - Parsing du format binaire carte GTech avec séparateurs de contrôle
 */

import { IBiometricService } from '../../application/ports/output/IBiometricService';
import { BiometricAuthResult } from '../../domain/aggregates/WithdrawalWorkflow';

const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const FINGERAUTH_BASE_URL = ENV.VITE_FINGERAUTH_BASE_URL ?? '';
const FINGERAUTH_ENDPOINT = '/Authentication';

export class RealBiometricFingerprintAdapter implements IBiometricService {
  private scanning = false;
  private cancelRequested = false;

  /**
   * Démarre le service biométrique
   */
  private async startService(): Promise<void> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/start`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Échec démarrage service biométrique: ${response.status}`);
      }

      const result = await response.json();
      if (result !== 1) {
        throw new Error('Service biométrique n\'a pas pu démarrer');
      }

      console.log('[Biométrie] Service démarré avec succès');
    } catch (error) {
      console.error('[Biométrie] Erreur démarrage:', error);
      throw error;
    }
  }

  /**
   * Arrête le service biométrique
   */
  private async stopService(): Promise<void> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/stop`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
      });

      if (response.ok) {
        console.log('[Biométrie] Service arrêté');
      }
    } catch (error) {
      console.error('[Biométrie] Erreur arrêt:', error);
    }
  }

  /**
   * Force un redémarrage complet du service Biocard
   *
   * CORRECTION CRITIQUE: Après un enrollment ou une vérification précédente,
   * le thread Biocard a done=true. Il faut forcer shutdown() puis start()
   * pour relancer le thread et ré-authentifier la carte.
   */
  private async forceRestartService(): Promise<void> {
    console.log('[Biométrie] Redémarrage forcé du service...');
    await this.stopService();
    await new Promise(resolve => setTimeout(resolve, 1500));
    await this.startService();
    console.log('[Biométrie] Service redémarré');
  }

  /**
   * Démarre un scan d'empreinte
   * CORRECTION: Force un redémarrage pour s'assurer que Biocard ré-authentifie la carte
   */
  async startFingerprintScan(): Promise<void> {
    this.scanning = true;
    this.cancelRequested = false;
    await this.forceRestartService();
    console.log('[Biométrie] Scanner d\'empreinte démarré');
  }

  /**
   * Effectue la vérification biométrique
   * Appelle /Authentication/verification
   */
  private async performVerification(): Promise<string> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/verification`;

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Échec vérification: ${response.status}`);
    }

    const result = await response.text();
    return result;
  }

  /**
   * Récupère les données de la carte
   * IMPORTANT: L'API retourne les données encodées en Base64
   */
  private async getCardData(): Promise<string> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/getCardData`;

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Échec lecture données carte: ${response.status}`);
    }

    const cardData = await response.text();
    return cardData;
  }

  /**
   * Récupère le numéro de la carte
   * IMPORTANT: L'API retourne le numéro encodé en Base64
   */
  // @ts-expect-error Méthode conservée pour compatibilité future (lecture séparée du numCarte)
  private async getCardNum(): Promise<string> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/getCardNum`;

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Échec lecture numéro carte: ${response.status}`);
    }

    const cardNum = await response.text();
    return cardNum.trim();
  }

  /**
   * Décode une chaîne Base64 en texte UTF-8
   * L'API FingerAuth (FingerImpl) encode toutes les données carte en Base64
   */
  private decodeBase64(base64Str: string): string {
    try {
      // Nettoyer la chaîne (retirer espaces, retours à la ligne)
      const cleaned = base64Str.trim();
      if (!cleaned) return '';

      // Décoder le Base64 en bytes puis en texte UTF-8
      const binaryString = atob(cleaned);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Retirer les octets nuls de padding (la carte padde à 240 bytes avec des 0x00)
      let end = bytes.length;
      while (end > 0 && bytes[end - 1] === 0) {
        end--;
      }

      const decoder = new TextDecoder('utf-8', { fatal: false });
      return decoder.decode(bytes.slice(0, end));
    } catch (error) {
      console.warn('[Biométrie] Échec décodage Base64:', error);
      return base64Str; // Retourner tel quel en fallback
    }
  }

  /**
   * Parse les données binaires de la carte (format length-prefixed UNACOOPEC)
   *
   * Format d'encodage backend (CarteEncodageService.ecrireChamp):
   *   Chaque champ = [1 octet longueur][données UTF-8]
   *   Si longueur = 0, le champ est vide.
   *
   * Ordre des champs (encoderDonneesCarte — aligné avec ancien encoded.js):
   *   0: nom          (ex: "YAO")
   *   1: prenoms      (ex: "Amenan Rose")
   *   2: pin          (ex: "CMD-2024-001" — code commande = identifiant CBS)
   *   3: dateExpiration (ex: "31/12/2028")
   *   4: dateEtablissement (ex: "01/01/2024")
   *   5: typeCompte   (ex: "EPARGNE")
   *   6: profil       (ex: "STANDARD")
   *
   * Note: le numCarte est encodé séparément via encoderNumCarte/setCardNum
   */
  private parseBinaryCardData(bytes: Uint8Array): {
    accountNumber?: string;
    clientName?: string;
    pin?: string;
    numCarte?: string;
  } | null {
    try {
      // Retirer les zéros de padding à la fin
      let end = bytes.length;
      while (end > 0 && bytes[end - 1] === 0) end--;
      if (end < 3) return null;

      const data = bytes.slice(0, end);
      const decoder = new TextDecoder('utf-8', { fatal: false });

      // ═══ Méthode 1: Parsing length-prefixed (format réel de ecrireChamp) ═══
      // Chaque champ: [1 octet = longueur N][N octets = données UTF-8]
      const fields: string[] = [];
      let offset = 0;

      while (offset < data.length && fields.length < 8) {
        const fieldLen = data[offset]; // Longueur du champ (0-255)
        offset += 1;

        if (offset + fieldLen > data.length) {
          // Données tronquées, on arrête
          break;
        }

        if (fieldLen === 0) {
          fields.push(''); // Champ vide
        } else {
          const fieldBytes = data.slice(offset, offset + fieldLen);
          const fieldText = decoder.decode(fieldBytes).trim();
          fields.push(fieldText);
          offset += fieldLen;
        }
      }

      console.log('[Biométrie] Champs carte (length-prefixed):', fields);

      // Format: [0]=nom, [1]=prenoms, [2]=pin, [3]=dateExp, [4]=dateEtab, [5]=type, [6]=profil, [7]=numCarte
      if (fields.length >= 3 && fields[0].length > 0) {
        const nom = fields[0] || '';       // ex: "YAO"
        const prenoms = fields[1] || '';   // ex: "Amenan Rose"
        const pin = fields[2] || '';       // ex: "CMD-2024-001" (code commande = identifiant CBS)
        const numCarte = fields[7] || '';  // ex: "1234567890123456" (numéro de carte PAN)

        const clientName = [nom, prenoms].filter(Boolean).join(' ') || 'Porteur de carte';

        console.log(`[Biométrie] ✅ Parsing OK: Nom="${nom}", Prénoms="${prenoms}", PIN="${pin}", NumCarte="${numCarte}"`);

        return {
          accountNumber: pin,   // Le PIN sert d'identifiant client CBS
          clientName,
          pin,
          numCarte,
        };
      }

      // ═══ Méthode 2: Fallback — split par caractères de contrôle ═══
      // Au cas où le format ne serait pas length-prefixed (ancienne carte ?)
      console.log('[Biométrie] Fallback: parsing par séparateurs de contrôle');
      const fallbackFields: string[] = [];
      let fieldStart = 0;

      for (let i = 0; i < data.length; i++) {
        const b = data[i];
        // Tous les octets < 0x20 (espace) sauf les printables UTF-8 sont des séparateurs
        if (b < 0x20) {
          if (i > fieldStart) {
            const fieldText = decoder.decode(data.slice(fieldStart, i)).trim();
            if (fieldText) fallbackFields.push(fieldText);
          }
          fieldStart = i + 1;
        }
      }
      if (fieldStart < data.length) {
        const fieldText = decoder.decode(data.slice(fieldStart)).trim();
        if (fieldText) fallbackFields.push(fieldText);
      }

      console.log('[Biométrie] Champs carte (fallback):', fallbackFields);

      if (fallbackFields.length < 2) return null;

      // Chercher le numéro de compte
      const accountIdx = fallbackFields.findIndex(f => /^\d{5,}$/.test(f));
      const result: { accountNumber?: string; clientName?: string } = {};

      if (accountIdx >= 0) {
        result.accountNumber = fallbackFields[accountIdx];
      }

      // Les champs texte avant le numéro de compte sont nom/prenoms
      const textBefore = fallbackFields
        .slice(0, accountIdx >= 0 ? accountIdx : fallbackFields.length)
        .filter(f => /^[A-Za-zÀ-ÿ\s'-]{2,}$/.test(f));

      if (textBefore.length >= 2) {
        result.clientName = `${textBefore[0]} ${textBefore[1]}`;
      } else if (textBefore.length === 1) {
        result.clientName = textBefore[0];
      }

      return (result.accountNumber || result.clientName) ? result : null;
    } catch (error) {
      console.error('[Biométrie] Erreur parsing binaire:', error);
      return null;
    }
  }

  /**
   * Parse les données de la carte pour extraire les informations client
   *
   * CORRECTION: Les données arrivent encodées en Base64 depuis FingerImpl.
   * Supporte 3 formats:
   * 1. Format binaire GTech (séparateurs de contrôle) — format réel
   * 2. JSON
   * 3. Format clé:valeur
   */
  private parseCardData(cardDataBase64: string): { clientId: string; clientName: string } | null {
    try {
      const cleaned = cardDataBase64.trim();
      if (!cleaned) return null;

      // Étape 1: Décoder le Base64 en bytes bruts
      let rawBytes: Uint8Array;
      try {
        const binaryString = atob(cleaned);
        rawBytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          rawBytes[i] = binaryString.charCodeAt(i);
        }
      } catch {
        rawBytes = new TextEncoder().encode(cleaned);
      }

      // Étape 2: Tentative parsing binaire GTech (format réel observé)
      const binaryResult = this.parseBinaryCardData(rawBytes);
      if (binaryResult) {
        // Priorité : numCarte (champ 8) comme clientId pour identifier le compte CBS
        const clientId = binaryResult.numCarte || binaryResult.accountNumber || binaryResult.clientName || 'CARD-USER';
        const clientName = binaryResult.clientName || 'Porteur de carte';
        console.log(`[Biométrie] Données carte (format binaire): ${clientName} / ${clientId}`);
        return { clientId, clientName };
      }

      // Étape 3: Décoder en texte pour JSON / clé:valeur
      const cardData = this.decodeBase64(cardDataBase64);
      console.log('[Biométrie] Données carte décodées:', cardData.substring(0, 100));
      if (!cardData) return null;

      // Tentative JSON
      try {
        const parsed = JSON.parse(cardData);
        if (parsed.clientId && parsed.clientName) {
          return { clientId: parsed.clientId, clientName: parsed.clientName };
        }
        if (parsed.numcpt || parsed.compte || parsed.accountNumber) {
          return {
            clientId: parsed.numcpt || parsed.compte || parsed.accountNumber,
            clientName: parsed.nom || parsed.name || parsed.clientName || 'Client',
          };
        }
      } catch {
        // Pas du JSON
      }

      // Tentative clé:valeur
      const parts = cardData.split('|');
      const data: Record<string, string> = {};
      for (const part of parts) {
        const sepIndex = part.indexOf(':');
        if (sepIndex > 0) {
          const key = part.substring(0, sepIndex).trim();
          const value = part.substring(sepIndex + 1).trim();
          if (key && value) data[key] = value;
        }
      }
      if (data.clientId && data.clientName) {
        return { clientId: data.clientId, clientName: data.clientName };
      }
      if (data.numcpt || data.compte) {
        return {
          clientId: data.numcpt || data.compte || '',
          clientName: data.nom || data.name || 'Client',
        };
      }

      // Étape 4: Chercher un numéro de compte par regex dans le texte brut
      const accountMatch = cardData.match(/\d{13,}/);
      if (accountMatch) {
        return {
          clientId: accountMatch[0],
          clientName: cardData.replace(accountMatch[0], '').trim().substring(0, 100) || 'Client',
        };
      }

      // Fallback: texte brut comme identifiant
      if (cardData.length > 0 && cardData.length < 500) {
        return {
          clientId: cardData.substring(0, 64),
          clientName: cardData.substring(0, 100),
        };
      }

      return null;
    } catch (error) {
      console.error('[Biométrie] Erreur parsing données carte:', error);
      return null;
    }
  }

  /**
   * Attend le résultat du scan avec tentatives de vérification
   *
   * CORRECTION: Après succès de la vérification biométrique,
   * même si la lecture des données carte échoue, on retourne
   * un succès avec un identifiant généré (la vérification d'empreinte
   * a bien réussi, les données client seront complétées par le
   * compte déjà chargé lors de l'insertion de la carte).
   */
  async waitForScan(timeoutMs = 30000): Promise<BiometricAuthResult> {
    const startTime = Date.now();
    const POLL_DELAY_MS = 500;

    // Polling sérialisé : chaque tentative attend la précédente avant de partir.
    // Évite l'empilement de requêtes parallèles côté backend Java/Securgen
    // (le driver natif n'est pas thread-safe — appels concurrents = JVM crash).
    return new Promise((resolve) => {
      let stopped = false;

      const finish = async (result: BiometricAuthResult) => {
        if (stopped) return;
        stopped = true;
        this.scanning = false;
        await this.stopService();
        resolve(result);
      };

      const tick = async () => {
        if (stopped) return;

        if (Date.now() - startTime > timeoutMs) {
          console.error('[Biométrie] Timeout dépassé');
          await finish({ success: false, errorCode: 'SCAN_TIMEOUT', timestamp: new Date() });
          return;
        }

        if (this.cancelRequested) {
          console.log('[Biométrie] Scan annulé');
          await finish({ success: false, errorCode: 'SCAN_CANCELLED', timestamp: new Date() });
          return;
        }

        let success = false;
        try {
          const verificationResult = await this.performVerification();
          if (verificationResult === '1' || verificationResult === 'true') {
            success = true;
          } else {
            const score = parseInt(verificationResult, 10);
            if (!isNaN(score) && score >= 70) success = true;
          }
        } catch {
          // 500 attendu tant que le doigt n'est pas posé — on retentera.
          console.debug('[Biométrie] Vérification en cours...');
        }

        if (stopped) return;

        if (success) {
          const authId = `AUTH-${Date.now()}-BIO`;
          let clientId = 'CARD-USER';
          let clientName = 'Porteur de carte';

          try {
            const cardDataBase64 = await this.getCardData();
            const clientInfo = this.parseCardData(cardDataBase64);
            if (clientInfo) {
              clientName = clientInfo.clientName;
              clientId = clientInfo.clientId;
              console.log(`[Biométrie] Empreinte reconnue: ${clientName} (carte: ${clientId})`);
            }
          } catch (error) {
            console.warn('[Biométrie] Lecture données carte échouée, utilisation identifiant par défaut:', error);
          }

          await finish({ success: true, clientId, clientName, authId, timestamp: new Date() });
          return;
        }

        // Replanifier la prochaine tentative seulement après celle-ci.
        setTimeout(tick, POLL_DELAY_MS);
      };

      tick();
    });
  }

  /**
   * Annule un scan en cours
   */
  cancelScan(): void {
    this.cancelRequested = true;
    console.log('[Biométrie] Demande d\'annulation du scan');
  }

  /**
   * Vérifie si le scanner est disponible
   */
  async isAvailable(): Promise<boolean> {
    try {
      const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/start`;
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
      });

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      return result === 1;
    } catch (error) {
      console.error('[Biométrie] Scanner non disponible:', error);
      return false;
    }
  }

  /**
   * Vérifie si un scan est en cours
   */
  isScanning(): boolean {
    return this.scanning;
  }
}
